// PRODUCTION — admin flow editor step drag-and-drop (within the same config group only)
import { useCallback, useState, type DragEvent } from 'react'
import type { OnboardingTemplate, OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'
import { getFlowEditorGroupSteps } from 'src/utils/collectDataTemplateEditor/insuranceSync'
import {
  computeInsertIndexInGroup,
  dropTargetsEqual,
  isDropTargetInDraggedStepGroup,
  isFlowEditorStepMovable,
  resolveDraggedStepConfigId,
  type FlowEditorStepDropTarget,
} from 'src/utils/collectDataTemplateEditor/stepReorder'
import { logger } from 'src/utils/logger'

interface UseStepDragDropParams {
  steps: OnboardingTemplateStep[]
  template: OnboardingTemplate | null
  /** Reorder `stepId` to `toIndex` within `configLocalId` (dispatches a moveStep op). */
  moveStepWithinGroup: (stepId: string, configLocalId: string, toIndex: number) => void
}

interface UseStepDragDropResult {
  draggedStepConfigId: string | null
  draggedStepId: string | null
  dropTarget: FlowEditorStepDropTarget | null
  handleDragStart: (stepId: string) => void
  handleDragOver: (event: DragEvent, dropTarget: FlowEditorStepDropTarget) => void
  handleDrop: (dropTarget: FlowEditorStepDropTarget) => void
  handleDragEnd: () => void
  isDropTarget: (dropTarget: FlowEditorStepDropTarget) => boolean
}

export function useStepDragDrop({
  steps,
  template,
  moveStepWithinGroup,
}: UseStepDragDropParams): UseStepDragDropResult {
  const [draggedStepId, setDraggedStepId] = useState<string | null>(null)
  const [draggedStepConfigId, setDraggedStepConfigId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<FlowEditorStepDropTarget | null>(null)

  const resetDragState = useCallback(() => {
    setDraggedStepId(null)
    setDraggedStepConfigId(null)
    setDropTarget(null)
  }, [])

  const handleDragStart = useCallback(
    (stepId: string) => {
      setDraggedStepId(stepId)
      setDropTarget(null)

      if (!template) {
        setDraggedStepConfigId(null)

        return
      }

      setDraggedStepConfigId(resolveDraggedStepConfigId(stepId, steps, template))
    },
    [steps, template],
  )

  const handleDragOver = useCallback(
    (event: DragEvent, nextDropTarget: FlowEditorStepDropTarget) => {
      event.preventDefault()

      if (!draggedStepId || !draggedStepConfigId) {
        return
      }

      if (!isDropTargetInDraggedStepGroup(draggedStepConfigId, nextDropTarget)) {
        return
      }

      if (dropTargetsEqual(dropTarget, nextDropTarget)) {
        return
      }

      setDropTarget(nextDropTarget)
    },
    [draggedStepConfigId, draggedStepId, dropTarget],
  )

  const handleDrop = useCallback(
    (nextDropTarget: FlowEditorStepDropTarget) => {
      const draggedId = draggedStepId

      resetDragState()

      if (!draggedId || !template) {
        return
      }

      const draggedStep = steps.find((step) => {
        return step.id === draggedId
      })

      if (!draggedStep || !isFlowEditorStepMovable(draggedStep)) {
        logger.warn('Flow editor step move blocked', { draggedStepId: draggedId })

        return
      }

      const groupConfigId = resolveDraggedStepConfigId(draggedId, steps, template)

      if (
        groupConfigId === null ||
        !isDropTargetInDraggedStepGroup(groupConfigId, nextDropTarget)
      ) {
        return
      }

      const groupSteps = getFlowEditorGroupSteps(steps, template, groupConfigId)
      const sourceIndex = groupSteps.findIndex((step) => step.id === draggedId)

      if (sourceIndex < 0) {
        return
      }

      const toIndex = computeInsertIndexInGroup(sourceIndex, nextDropTarget.indexInGroup)

      if (toIndex === sourceIndex) {
        return
      }

      moveStepWithinGroup(draggedId, groupConfigId, toIndex)
    },
    [draggedStepId, moveStepWithinGroup, resetDragState, steps, template],
  )

  const handleDragEnd = useCallback(() => {
    resetDragState()
  }, [resetDragState])

  const isDropTarget = useCallback(
    (candidate: FlowEditorStepDropTarget) => {
      return dropTargetsEqual(dropTarget, candidate)
    },
    [dropTarget],
  )

  return {
    draggedStepConfigId,
    draggedStepId,
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    isDropTarget,
  }
}
